import React, { useMemo, useState } from "react";
import { Alert, Box, Button, Fab, Paper, Stack, TextField, Typography } from "@mui/material";
import ChatBubbleOutlineIcon from "@mui/icons-material/ChatBubbleOutline";
import CloseIcon from "@mui/icons-material/Close";
import SendIcon from "@mui/icons-material/Send";
import { apiClient, toApiErrorMessage } from "../../shared/services/api";

const FAQ_RESPONSES = [
  {
    keywords: ["book", "appointment", "schedule"],
    answer: "To book an appointment, open Appointments and choose the pet, doctor, date, and slot. Pet owners can request appointments for their own pets only."
  },
  {
    keywords: ["vaccine", "vaccination", "booster", "due"],
    answer: "Vaccination suggestions are shown on pet history and dashboards. Clinics can schedule or record administered vaccines from the Vaccinations workspace."
  },
  {
    keywords: ["bill", "invoice", "payment", "pay"],
    answer: "Invoices are managed in Billing. You can record offline payment or use Pay Online to complete Razorpay payment and update invoice status."
  },
  {
    keywords: ["lab", "result", "test"],
    answer: "Doctors can request lab tests from a visit. Lab results can create a diagnosis or be added directly to an invoice."
  },
  {
    keywords: ["history", "record", "pet"],
    answer: "Open a pet profile and select History to see visits, SOAP notes, diagnoses, prescriptions, lab results, vaccinations, invoices, and a PDF export."
  }
];

const localAnswerFor = (question) => {
  const normalized = question.toLowerCase();
  return FAQ_RESPONSES.find((item) => item.keywords.some((keyword) => normalized.includes(keyword)))?.answer;
};

const ChatWidget = () => {
  const [open, setOpen] = useState(false);
  const [message, setMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [messages, setMessages] = useState([
    {
      role: "assistant",
      content: "Hi, I am VetCastle Assist. Ask me about booking, vaccinations, billing, labs, or pet history."
    }
  ]);

  const quickPrompts = useMemo(() => ["Book appointment", "Vaccination due", "Pay invoice"], []);

  const ask = async (text = message) => {
    const question = text.trim();
    if (!question) return;

    setMessage("");
    setError("");
    setMessages((current) => [...current, { role: "user", content: question }]);
    setLoading(true);

    try {
      const localAnswer = localAnswerFor(question);
      if (localAnswer) {
        setMessages((current) => [...current, { role: "assistant", content: localAnswer }]);
        return;
      }

      const response = await apiClient.post("/chatbot/ask", { message: question, question });
      const answer = response.data?.data?.answer || response.data?.answer || response.data?.message || "I can help with clinic workflows, appointments, billing, and pet records.";
      setMessages((current) => [...current, { role: "assistant", content: answer }]);
    } catch (err) {
      setError(toApiErrorMessage(err, "Chatbot is temporarily unavailable"));
      setMessages((current) => [...current, { role: "assistant", content: "I could not reach the assistant service, but I can still answer common workflow questions." }]);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Fab
        color="primary"
        aria-label="Open VetCastle assistant"
        onClick={() => setOpen((current) => !current)}
        sx={{ position: "fixed", right: 24, bottom: 24, zIndex: 1300 }}
      >
        {open ? <CloseIcon /> : <ChatBubbleOutlineIcon />}
      </Fab>

      {open && (
        <Paper
          elevation={12}
          sx={{
            position: "fixed",
            right: 24,
            bottom: 92,
            width: { xs: "calc(100vw - 48px)", sm: 380 },
            maxHeight: "70vh",
            zIndex: 1300,
            borderRadius: 4,
            overflow: "hidden"
          }}
        >
          <Box sx={{ p: 2, bgcolor: "#19585E", color: "white" }}>
            <Typography fontWeight={900}>VetCastle Assist</Typography>
            <Typography variant="body2" sx={{ opacity: 0.85 }}>Workflow help for clinic teams and pet owners</Typography>
          </Box>

          <Stack spacing={1.5} sx={{ p: 2, maxHeight: 360, overflowY: "auto" }}>
            {messages.map((item, index) => (
              <Box
                key={`${item.role}-${index}`}
                sx={{
                  alignSelf: item.role === "user" ? "flex-end" : "flex-start",
                  maxWidth: "85%",
                  p: 1.25,
                  borderRadius: 3,
                  bgcolor: item.role === "user" ? "primary.main" : "grey.100",
                  color: item.role === "user" ? "primary.contrastText" : "text.primary"
                }}
              >
                <Typography variant="body2">{item.content}</Typography>
              </Box>
            ))}
            {error && <Alert severity="warning">{error}</Alert>}
          </Stack>

          <Stack direction="row" spacing={1} sx={{ px: 2, pb: 1 }}>
            {quickPrompts.map((prompt) => (
              <Button key={prompt} size="small" variant="outlined" onClick={() => ask(prompt)} disabled={loading}>
                {prompt}
              </Button>
            ))}
          </Stack>

          <Stack direction="row" spacing={1} sx={{ p: 2, pt: 0 }}>
            <TextField
              size="small"
              placeholder="Ask about a workflow..."
              value={message}
              onChange={(event) => setMessage(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === "Enter") ask();
              }}
              fullWidth
            />
            <Button variant="contained" endIcon={<SendIcon />} disabled={loading || !message.trim()} onClick={() => ask()}>
              Send
            </Button>
          </Stack>
        </Paper>
      )}
    </>
  );
};

export default ChatWidget;
